import React, { useEffect, useState } from "react";
import { api, resolveMedia } from "@/lib/api";
import { SectionTitle } from "@/components/SectionTitle";
import { Archive, Trophy, Crown, Medal, CaretLeft } from "@phosphor-icons/react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" }) : "");

function SeasonDetail({ season, onBack }) {
  const champions = season.champions || {};
  const leaders = season.stat_leaders || {};
  const STAT_ORDER = [["points", "Points"], ["rebounds", "Rebounds"], ["assists", "Assists"], ["blocks", "Blocks"], ["steals", "Steals"], ["threes", "3PM"]];

  return (
    <div data-testid="season-detail">
      <button onClick={onBack} data-testid="season-back-btn" className="flex items-center gap-1.5 text-xs uppercase font-bold tracking-wide text-white/60 hover:text-[#FF5722] transition-colors mb-6"><CaretLeft size={14} /> All Seasons</button>
      <h2 className="font-display text-4xl sm:text-5xl uppercase mb-1">{season.title}</h2>
      <p className="text-white/40 text-sm mb-10">Season ended {fmtDate(season.ended_at)} · {season.counts?.games_completed || 0} games played</p>

      {Object.keys(champions).length > 0 && (
        <div className="mb-12">
          <h3 className="font-display text-2xl uppercase mb-4 flex items-center gap-2"><Crown size={22} weight="fill" className="text-[#FF5722]" /> Champions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.entries(champions).map(([div, name]) => (
              <div key={div} className="border border-[#FF5722]/40 bg-gradient-to-br from-[#FF5722]/15 to-transparent p-5">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF5722] font-bold">{div}</p>
                <p className="font-display text-2xl uppercase mt-1">{name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {(season.standings || []).length > 0 && (
        <div className="mb-12">
          <h3 className="font-display text-2xl uppercase mb-4 flex items-center gap-2"><Trophy size={20} weight="fill" className="text-[#FF5722]" /> Final Standings</h3>
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full text-sm min-w-[520px]">
              <thead><tr className="bg-[#151515] text-white/50 uppercase text-[10px] tracking-wide">
                <th className="text-left px-4 py-2">Team</th><th className="px-3 py-2">Div</th><th className="px-3 py-2">W</th><th className="px-3 py-2">L</th><th className="px-3 py-2">PF</th><th className="px-3 py-2">PA</th><th className="px-3 py-2">PD</th>
              </tr></thead>
              <tbody>
                {season.standings.map((r, i) => (
                  <tr key={r.team_id || i} className="border-t border-white/5">
                    <td className="text-left px-4 py-2 font-semibold uppercase">{r.team_name}</td>
                    <td className="text-center px-3 py-2 text-white/40 text-xs">{r.division}</td>
                    <td className="text-center px-3 py-2 tabular-nums">{r.w}</td>
                    <td className="text-center px-3 py-2 tabular-nums">{r.l}</td>
                    <td className="text-center px-3 py-2 tabular-nums text-white/60">{r.pf}</td>
                    <td className="text-center px-3 py-2 tabular-nums text-white/60">{r.pa}</td>
                    <td className={`text-center px-3 py-2 tabular-nums ${r.pd > 0 ? "text-[#00E676]" : r.pd < 0 ? "text-[#FF3B30]" : ""}`}>{r.pd > 0 ? `+${r.pd}` : r.pd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mb-12">
        <h3 className="font-display text-2xl uppercase mb-4 flex items-center gap-2"><Medal size={20} className="text-[#FF5722]" /> Stat Leaders</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {STAT_ORDER.map(([key, label]) => {
            const rows = (leaders[key] || []).slice(0, 5);
            if (!rows.length) return null;
            return (
              <div key={key} className="border border-white/10 bg-[#121212] p-4">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF5722] font-bold mb-3">{label}</p>
                {rows.map((r, i) => (
                  <div key={r.player_id || i} className="flex items-center gap-2 py-1 text-sm">
                    <span className={`font-display w-4 ${i === 0 ? "text-[#FF5722]" : "text-white/40"}`}>{i + 1}</span>
                    <span className="flex-1 min-w-0 truncate">{r.name}<span className="text-white/30 text-xs"> · {r.team_name}</span></span>
                    <span className="font-display text-[#FF5722] tabular-nums">{r.value}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {(season.highlights || []).length > 0 && (
        <div>
          <h3 className="font-display text-2xl uppercase mb-4 flex items-center gap-2"><Archive size={20} className="text-[#FF5722]" /> Highlights</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {season.highlights.map((h) => (
              <a key={h.id} href={h.media_type === "link" ? h.url : resolveMedia(h.url)} target="_blank" rel="noopener noreferrer" className="group border border-white/10 bg-[#121212] overflow-hidden">
                <div className="aspect-video bg-[#0A0A0A] overflow-hidden">
                  {(h.thumbnail || h.media_type === "image")
                    ? <img src={resolveMedia(h.thumbnail || h.url)} alt={h.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    : <div className="w-full h-full flex items-center justify-center text-white/20 text-xs uppercase">{h.media_type}</div>}
                </div>
                <p className="text-xs text-white/70 px-2 py-2 truncate">{h.title}</p>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PastSeasons() {
  const [seasons, setSeasons] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/seasons").then((r) => setSeasons(r.data || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  const open = (id) => api.get(`/seasons/${id}`).then((r) => setActive(r.data)).catch(() => {});

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="past-seasons-page">
      {!active && <SectionTitle overline="The archives" title="Past Seasons" sub="Relive finished seasons — final standings, champions, statistical leaders and highlights." />}

      {active ? (
        <SeasonDetail season={active} onBack={() => setActive(null)} />
      ) : loading ? (
        <p className="text-white/40 mt-8">Loading…</p>
      ) : seasons.length === 0 ? (
        <div className="border-2 border-dashed border-white/15 py-20 text-center mt-8" data-testid="past-seasons-empty">
          <Archive size={44} className="text-white/25 mx-auto mb-4" />
          <p className="text-white/50">No seasons have been archived yet. The current season is still in play!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {seasons.map((s) => (
            <button key={s.id} onClick={() => open(s.id)} data-testid={`season-card-${s.id}`} className="text-left border border-white/10 bg-[#121212] p-6 hover:border-[#FF5722] transition-colors duration-200 group">
              <Archive size={28} className="text-[#FF5722] mb-3" />
              <p className="font-display text-3xl uppercase group-hover:text-[#FF5722] transition-colors">{s.title}</p>
              <p className="text-xs text-white/40 mt-1">Ended {fmtDate(s.ended_at)}</p>
              <p className="text-xs text-white/50 mt-3">{s.counts?.games_completed || 0} games · {s.counts?.teams || 0} teams · {s.counts?.players || 0} players</p>
              {Object.keys(s.champions || {}).length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-x-3 gap-y-1">
                  {Object.entries(s.champions).map(([div, name]) => (
                    <span key={div} className="text-xs text-white/70 flex items-center gap-1"><Crown size={12} weight="fill" className="text-[#FF5722]" />{name}</span>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
